/**
 * DEPOSITS AS A POLICY, NOT AS THREE BOXES ON EVERY HOME.
 *
 * WHY THIS EXISTS, MEASURED RATHER THAN ARGUED. The deposit used to be three figures typed on each
 * property. On 2026-09-26 the live database held **42 properties with all three bands null** and
 * three of forty-three with a `configured_at` — nobody had ever filled it in, for anything. That is
 * what a policy typed once per home always produces, and it is the same argument that produced
 * pricing strategies: with 126 homes, putting the policy on each property means typing it 126 times
 * and then being unable to change it once.
 *
 * So this is the same shape as `pricing/strategy.js`, deliberately, so there is one idea to learn
 * rather than two: a named policy owns a list of homes, the highest band reached wins and bands
 * never stack.
 *
 * THE PRODUCT OWNER'S DESIGN, 2026-09-27:
 *
 *   我可以选择…1 个月、3 个月、6~12 个月各是月租金的百分之多少或者是绝对金额的多少。
 *   然后我可以有多种的 property 押金的一个设定方式…我一个 Property 可以选择我要套用哪一个押金。
 *
 * and, the next day, the piece that makes percentages usable at all:
 *
 *   有的时候他租金太低的时候，比如说租金是 3500，然后他租半年，然后我们如果是按 50% 的话，
 *   其实是不到 2000，可是我们有一个最低要收 2000。
 */


/** A band is a percentage of the monthly rent, or a flat figure. Nothing else. */
export const BASIS = { PERCENT: 'PERCENT', AMOUNT: 'AMOUNT' }

export const BASIS_LABEL = {
  PERCENT: '% of rent',
  AMOUNT: 'Amount',
}

/**
 * Make one.
 *
 * `appliesTo` is the policy's own list of homes, the way a pricing strategy owns its listings. The
 * home does not name the policy; the policy names the homes. That is what makes "change the policy
 * for everything on it" one edit.
 */
export const createPolicy = ({ id, name, bands = [], appliesTo = [] }) =>
  ({ id, name, bands, appliesTo })

/**
 * A band.
 *
 * `minimum` is a FLOOR IN DOLLARS and may be null, which means no floor. It exists because a
 * percentage of a low rent is a deposit this business will not accept: 50% of $3,500 is $1,750 and
 * the floor is $2,000.
 *
 * A FLOOR ON AN `AMOUNT` BAND IS KEPT AND NOT APPLIED. Kept, because a value that vanishes when
 * somebody switches a band from percent to amount and back is worse than one sitting there inert.
 * Not applied, because an amount IS the figure — there is nothing for a floor to lift, and a
 * $1,500 amount band silently charging $2,000 from a leftover minimum is the exact defect a
 * reviewer found in the first version of this (mtr#95). The screen disables the box; `depositFor`
 * is what makes it true.
 */
export const createBand = ({ months, basis = BASIS.PERCENT, value = null, minimum = null }) =>
  ({ months, basis, value, minimum })

const isNum = (v) => Number.isFinite(v)

/**
 * THE BAND FOR A STAY OF `months` WHOLE MONTHS. The highest one reached wins; bands never stack.
 *
 * A six-month stay that passes a 1-, 3- and 6-month row takes the 6-month row only — the same rule
 * `monthTierFor` keeps for the rent ladder, and for the same reason: adding them would compound
 * figures nobody chose to compound.
 */
export function bandFor(policy, months) {
  const bands = policy?.bands
  if (!Array.isArray(bands) || !isNum(months)) return null
  /*
    TWO ROWS WITH THE SAME THRESHOLD ARE A QUESTION THIS FUNCTION CANNOT ANSWER. (Copilot, mtr#95.)

    `sort` is stable, so among equal `months` the first row in the list wins — which means dragging
    two rows past each other would change the deposit actually charged, with nothing on screen
    saying anything moved. That is not a tie-break worth inventing: picking "the bigger one" or
    "the later one" would be an arbitrary rule nobody chose, and it would make the ambiguity
    permanent by making it survivable.

    So the ambiguity is refused where it is created instead: `policyProblems` reports duplicate
    thresholds and the editor will not save a policy that has one. This function stays total for
    data that already exists — a policy loaded from somewhere else with a duplicate still returns
    an answer rather than throwing inside a render — and the screen shows it as a problem.
  */
  return bands
    .filter((b) => isNum(b?.months) && months >= b.months)
    .sort((a, b) => b.months - a.months)[0] ?? null
}

/**
 * WHAT IS WRONG WITH THIS POLICY, IF ANYTHING — the list the Save button is disabled by.
 *
 * Only structural faults live here: things that make the policy's own answer ambiguous or
 * unanswerable, not things somebody merely has not finished typing. A legal cap is NOT a problem in
 * this sense and deliberately does not appear — the Product Owner rejected making a capped home
 * unsaveable ("one cheap home should not make a policy that is right for forty others
 * unsaveable"), so that stays a warning and this stays a refusal.
 *
 * Returns `[{ kind, months?, index? }]`, empty when the policy is sound.
 */
export const PROBLEM = {
  DUPLICATE_MONTHS: 'DUPLICATE_MONTHS',
  MONTHS_NOT_A_WHOLE_NUMBER: 'MONTHS_NOT_A_WHOLE_NUMBER',
  NEGATIVE_VALUE: 'NEGATIVE_VALUE',
  NEGATIVE_MINIMUM: 'NEGATIVE_MINIMUM',
  UNKNOWN_BASIS: 'UNKNOWN_BASIS',
  NOT_WHOLE_DOLLARS: 'NOT_WHOLE_DOLLARS',
}

/* A dollar figure this product will store. Percentages may be fractional; money may not. */
const wholeDollars = (v) => Number.isInteger(v)

export function policyProblems(policy) {
  const bands = Array.isArray(policy?.bands) ? policy.bands : []
  const out = []
  const seen = new Map()

  bands.forEach((b, index) => {
    const m = b?.months
    if (!Number.isInteger(m) || m < 1) {
      out.push({ kind: PROBLEM.MONTHS_NOT_A_WHOLE_NUMBER, index, months: m ?? null })
    } else if (seen.has(m)) {
      out.push({ kind: PROBLEM.DUPLICATE_MONTHS, months: m, index })
    } else {
      seen.set(m, index)
    }

    /*
      A NEGATIVE DEPOSIT IS MONEY OWED TO THE RENTER. (Copilot, mtr#95.)

      `min="0"` on a number input stops the spinner, not the keyboard: -50 can be typed straight
      in, and nothing downstream rejected it — `depositFor` would return a negative figure and a
      quote would offer to pay her to move in. `null` is not a problem here and deliberately so:
      an empty box is a band nobody has finished, which `depositFor` already answers with null.
    */
    if (b?.value != null && (!isNum(b.value) || b.value < 0)) {
      out.push({ kind: PROBLEM.NEGATIVE_VALUE, index, value: b.value })
    }
    if (b?.minimum != null && (!isNum(b.minimum) || b.minimum < 0)) {
      out.push({ kind: PROBLEM.NEGATIVE_MINIMUM, index, minimum: b.minimum })
    }

    /*
      HALF A CENT IS NOT A DEPOSIT. (Copilot, mtr#95.)

      `step` on a number input is a spinner hint, not a constraint: `100.005` types in fine, and an
      AMOUNT band carrying it produced `cents: 10000.5`. Every figure downstream — a quote, a
      receipt, a ledger row — is an integer number of cents, so a fractional one is a number that
      cannot be charged and will be silently rounded by whichever component prints it first.

      A PERCENTAGE MAY BE FRACTIONAL, because 12.5% of a rent is a perfectly ordinary rule and the
      percent path already rounds to a whole cent. It is the dollar fields that are refused.
    */
    if (b?.basis === BASIS.AMOUNT && b?.value != null && isNum(b.value) && !wholeDollars(b.value)) {
      out.push({ kind: PROBLEM.NOT_WHOLE_DOLLARS, index, amount: b.value })
    }
    if (b?.minimum != null && isNum(b.minimum) && b.minimum >= 0 && !wholeDollars(b.minimum)) {
      out.push({ kind: PROBLEM.NOT_WHOLE_DOLLARS, index, amount: b.minimum })
    }

    /*
      A band whose basis this version does not recognise cannot be saved, because nothing can say
      what it asks for. `depositFor` answers null for it rather than guessing at a percentage.
    */
    if (b?.basis !== BASIS.PERCENT && b?.basis !== BASIS.AMOUNT) {
      out.push({ kind: PROBLEM.UNKNOWN_BASIS, index, basis: b?.basis ?? null })
    }
  })

  return out
}

/**
 * What this policy actually asks for, on this home, for a stay of this length.
 *
 * Returns `null` when there is no answer — no policy, no band reached, no rent to take a percentage
 * of, or a band nobody has typed a value into. **A missing deposit is null and never zero.** Zero
 * is a decision somebody made; null is the absence of one, and a screen that prints $0 for the
 * second has told a renter something nobody ever said.
 *
 * Otherwise it returns every step of the arithmetic, because the screen has to be able to show
 * *why* — a figure that appears without its reasoning is a figure nobody checks:
 *
 *   `{ cents, band, computedCents, flooredBy }`
 *
 * Compute, then apply the floor. There used to be a third step — the city's legal ceiling — and it
 * now runs at the quote instead, where the fees and the actual figure exist (mtr#112).
 */
export function depositFor(policy, months, home = {}) {
  const { monthlyRent = null } = home
  const band = bandFor(policy, months)
  if (!band || !isNum(band.value)) return null

  /*
    A BASIS THIS MODULE DOES NOT KNOW IS NOT A PERCENTAGE. (Copilot, mtr#95.)

    `else` meant that anything which was not `AMOUNT` was treated as a percentage, so a band
    carrying `basis: 'INVALID'` — a legacy row, a typo, a future basis this version has not
    learned — quietly produced `value`% of the rent. A band saying 2000 would have asked for
    twenty times the rent, with nothing anywhere saying a word.

    Absence is never a verdict (AGENTS.md): an unrecognised basis returns null, the same as a band
    nobody has typed a value into, and `policyProblems` refuses to save it.
  */
  /*
    A DEPOSIT IS A WHOLE NUMBER OF DOLLARS, AND THAT IS ONE RULE RATHER THAN THREE ROUNDINGS.
    (Copilot, mtr#95 — it caught a test of mine whose message described cent rounding while the
    code rounded to dollars. The message was the thing that was wrong, and an assertion nobody
    executes is exactly the defect this repository keeps recording.)

    The rule is already visible elsewhere in this file: `policyProblems` REFUSES a fractional
    dollar figure in an Amount or Minimum box. Rounding a percentage to the dollar is that same
    rule applied to the one figure a person does not type — 12.5% of $3,501 is $437.625, and this
    product asks for $438. Every path is rounded once, here, and nothing downstream rounds again.
  */
  let computed = null
  if (band.basis === BASIS.AMOUNT) computed = Math.round(band.value)
  else if (band.basis === BASIS.PERCENT && isNum(monthlyRent)) computed = Math.round((monthlyRent * band.value) / 100)
  if (!isNum(computed)) return null

  let amount = computed
  let flooredBy = null
  /* PERCENT only — see `createBand`. An amount is the figure; there is nothing to lift. */
  if (band.basis === BASIS.PERCENT && isNum(band.minimum) && Math.round(band.minimum) > amount) {
    amount = Math.round(band.minimum)
    flooredBy = amount
  }

  /*
    THE LEGAL CAP USED TO BE APPLIED HERE, AND IT IS NOW ASKED AT THE QUOTE. (mtr#112.)

    The Product Owner, 2026-09-28: 「你不要现在就去算，你应该要发现跟他讲，这样可能是有 legal 的
    风险」, and the place to find it is 「在报价那里」.

    He is right, and the reason is stronger than the preference: **a policy is not a charge.** A
    band saying "one month's rent at twelve months" is unlawful only for a particular home, on a
    particular stay, once the fees actually being charged are added to it. Checking it here meant
    checking it on facts that were not in yet — which is exactly why seven of forty-two homes came
    out as "not fully checked" instead of as an answer, and why this function grew an
    `otherMoveInChargesCents` input nobody had a value for. A screen that half-answers a legal
    question teaches its reader to skip it.

    At the quote all of those facts exist, including an overridden deposit, which no check made
    here could ever see. `src/pricing/depositLaw.js` asks it there; `./depositCap.js`, which holds
    what the law actually says, is unchanged and is now its caller's.

    What stays here is arithmetic this policy really does own: the band, the percentage, the floor.
  */
  return {
    /* Whole dollars by the time it gets here, so this is exact — and rounded anyway, because a
       caller that never went through the editor is not this function's to trust. */
    cents: Math.round(amount * 100),
    band,
    computedCents: computed * 100,
    flooredBy,
  }
}

export function policyForUnit(policies, unitId) {
  return (policies ?? []).find((p) => (p.appliesTo ?? []).includes(unitId)) ?? null
}

/** Every home on a policy other than this one — what the assign dialog may not offer. */
export function takenByOthers(policies, policyId) {
  const taken = new Map()
  for (const p of policies ?? []) {
    if (p.id === policyId) continue
    for (const id of p.appliesTo ?? []) taken.set(id, p)
  }
  return taken
}
